import { useMemo, useState } from 'react';
import cm from 'classnames';

import { RenderStatus } from 'services/finalcut.service';
import { useAppDispatch, useAppSelector } from 'store/store-hooks';
import { IntroKeys } from 'components/app/intro/intro-keys';
import { ttShare } from 'components/tiktok/tiktok.slice';
import { ModalEnum, setModal } from 'components/app/_mediator/app.slice';
import { allowToShare } from 'components/app/finalcuts/_mediator/fc-data.slice';
import { useFcShare } from './_use-fc-share';

import { ReactComponent as ShareIco } from 'assets/ui/share-1.svg';
import { ReactComponent as YoutubeIco } from 'assets/social/youtube.svg';
import { ReactComponent as FacebookIco } from 'assets/social/facebook.svg';
import { ReactComponent as TikTokIco } from 'assets/social/tiktok.svg';

import { Modal } from 'components/UI/modal/modal';
import { Loader } from 'components/UI/loader/loader';
import { Button } from 'components/UI/button/button';
import { HoverModal, HoverModal__popup } from 'components/app/app-components/hover-modal/hover-modal';
import { AppButton } from 'components/app/app-components/app-button/app-button';
import styles from './_fc-share.module.scss';

enum SN {
  Facebook,
  YouTube,
  TikTok,
}

export const FcShare = ({ className }: React.HTMLAttributes<HTMLDivElement>) => {
  const { fbShare, ytShare, ytLogin, ytReLogin } = useFcShare();
  const { id, isCanShare, fcStatus } = useAppSelector((state) => state.fc__data);
  const youtubeFiles = useAppSelector((state) => state.app__filesUpload.youtubeFiles);
  const appModal = useAppSelector((state) => state.app.modal);
  const progress = id && youtubeFiles[id];
  const dispatch = useAppDispatch();

  const [sn, setSn] = useState<SN | null>(null);

  const isDisabled = useMemo(() => {
    return fcStatus !== RenderStatus.rendered;
  }, [fcStatus]);

  const onClick = (s: SN) => {
    if (isCanShare) share(s);
    else setSn(s);
  };

  const share = async (s: SN) => {
    if (id == null) return;
    if (!isCanShare) dispatch(allowToShare(id));
    if (s === SN.Facebook) fbShare();
    else if (s === SN.TikTok) dispatch(ttShare());
    else if (s === SN.YouTube && !progress) ytReLogin();
    setSn(null);
  };

  return (
    <HoverModal className={cm(styles.FcShare, className)} data-intro={IntroKeys.fcShare}>
      <AppButton
        disabled={isDisabled}
        className={cm(styles.FcShare__btn, { [styles.disabled]: isDisabled })}
        outlined
        tip="Share to social media"
        tipPosition="bottom"
      >
        {progress ? <Loader className={styles.FcShare__loader} /> : <ShareIco />}
      </AppButton>
      {!isDisabled && (
        <HoverModal__popup>
          <ul>
            <li className={styles.FcShare__fb} onClick={() => onClick(SN.Facebook)}>
              <span>
                <FacebookIco />
              </span>
              Facebook
            </li>
            <li onClick={() => onClick(SN.YouTube)}>
              <span>
                <YoutubeIco />
              </span>
              {progress ? (
                <>
                  <Loader className={styles.FcShare__loaderYt} />
                  {progress} %
                </>
              ) : (
                'Youtube'
              )}
            </li>
            <li onClick={() => onClick(SN.TikTok)}>
              <span>
                <TikTokIco />
              </span>
              TikTok
            </li>
          </ul>
        </HoverModal__popup>
      )}
      <Modal className={styles.AllowToShareModal} isOpen={sn !== null} onRequestClose={() => setSn(null)}>
        <h3>Do you want to share this final cut?</h3>
        <footer>
          <Button onClick={() => setSn(null)} outlined>
            No
          </Button>
          <Button onClick={() => share(sn!)}>Yes</Button>
        </footer>
      </Modal>

      <Modal
        className={styles.YtReLoginModal}
        isOpen={appModal === ModalEnum.ytReLogin}
        onRequestClose={() => dispatch(setModal(0))}
      >
        <YoutubeIco className={styles.YtReLoginModal__ico} />
        <h3>Share on YouTube</h3>
        <footer>
          <Button
            outlined
            onClick={() => {
              dispatch(setModal(0));
              ytLogin();
            }}
          >
            Switch Accounts
          </Button>
          <Button
            onClick={() => {
              dispatch(setModal(0));
              ytShare();
            }}
          >
            Yes
          </Button>
        </footer>
      </Modal>
    </HoverModal>
  );
};
